/**
 * SpecSnapshotHistory — "History" button + dropdown for restoring viz snapshots.
 * Used in both the operation viz chat panel and the report viz chat panel.
 *
 * ADR-0050 §4.12, §5.10
 */
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { formatTimestamp } from "../lib/formatTime.js";

type SpecSnapshotSummary = {
  id: string;
  change_source: "ai" | "user";
  changed_by: { id: string; display_name: string };
  change_description: string | null;
  created_at: string;
};

export function SpecSnapshotHistoryButton(props: {
  contextType: "operation_viz" | "report_viz";
  contextId: string;
  nodeId?: string;
  onRestored: (spec: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<SpecSnapshotSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = new URLSearchParams({
      context_type: props.contextType,
      context_id: props.contextId,
    });
    if (props.nodeId) params.set("node_id", props.nodeId);
    void apiFetch<{ snapshots: SpecSnapshotSummary[] }>(`/api/tenant/spec-snapshots?${params.toString()}`)
      .then((res) => setSnapshots(res.snapshots))
      .catch(() => setSnapshots([]))
      .finally(() => setLoading(false));
  }, [open, props.contextType, props.contextId, props.nodeId]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  async function restore(snapshotId: string) {
    setRestoringId(snapshotId);
    try {
      const res = await apiFetch<{ spec: unknown }>(`/api/tenant/spec-snapshots/${snapshotId}/restore`, {
        method: "POST",
        body: {},
      });
      props.onRestored(res.spec);
      setOpen(false);
    } catch {
      // silently ignore — parent handles re-render or error display
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        className="btn"
        type="button"
        title="View snapshot history"
        onClick={() => setOpen((v) => !v)}
        style={{ fontSize: 12, padding: "2px 8px" }}
      >
        History
      </button>
      {open && (
        <div style={{
          position: "absolute",
          bottom: "100%",
          right: 0,
          marginBottom: 4,
          width: 320,
          maxHeight: 300,
          overflowY: "auto",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          zIndex: 50,
        }}>
          <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", fontWeight: 600, fontSize: 12 }}>
            Snapshot History
          </div>
          {loading ? (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--fg-muted)" }}>Loading…</div>
          ) : snapshots.length === 0 ? (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--fg-muted)" }}>No snapshots yet.</div>
          ) : (
            snapshots.map((s) => (
              <div key={s.id} style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ fontSize: 14, lineHeight: 1 }}>{s.change_source === "ai" ? "🤖" : "✏️"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.change_description ?? "Manual edit"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
                    {s.changed_by.display_name} · {formatTimestamp(s.created_at)}
                  </div>
                </div>
                <button
                  className="btn"
                  type="button"
                  disabled={restoringId === s.id}
                  onClick={() => void restore(s.id)}
                  style={{ fontSize: 11, padding: "2px 8px", flexShrink: 0 }}
                >
                  {restoringId === s.id ? "…" : "Restore"}
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
